import { Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import {
  mdiAlertCircleOutline,
  mdiAlertOutline,
  mdiDelete,
  mdiDotsVertical,
  mdiPlus,
} from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import TextInput from 'components/TextInput/TextInput';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SwitchItem from './SwitchItem';

const AssignmentItem = (props) => {
  const {
    loading,
    assignment,
    childAssignments,
    assignments,
    handleChangeAssignment,
    handleChangeValueOfAssignmentAndAllChildren,
    handleAddAssignment,
    handleDeleteAssignment,
    showAssignmentNumberPath,
    parentNumber,
    parentAssignment,
    openAssignmentsArray,
    setOpenAssignmentsArray,
    updatedAssignmentIds,
  } = props;
  const { t } = useTranslation('common');

  const theme = useTheme();

  const hasChanges = useMemo(
    () => updatedAssignmentIds.includes(assignment.id),
    [updatedAssignmentIds]
  );

  const open = useMemo(
    () => openAssignmentsArray.includes(assignment.id),
    [openAssignmentsArray]
  );

  const handleOpen = () => {
    if (open) {
      setOpenAssignmentsArray(
        openAssignmentsArray.filter((id) => id !== assignment.id)
      );
    } else {
      setOpenAssignmentsArray([...openAssignmentsArray, assignment.id]);
    }
  };

  const onChange = (e) => {
    const newAssignment = { ...assignment, [e.target.name]: e.target.value };
    handleChangeAssignment(newAssignment);
  };

  const onChangeNonEvent = (name, value) => {
    handleChangeValueOfAssignmentAndAllChildren(assignment, name, value);
  };

  const addChildAssignment = (e) => {
    e.stopPropagation();
    handleAddAssignment(assignment.id);
  };

  const numberPath = parentNumber
    ? parentNumber + '.' + assignment.number
    : assignment.number;

  const { requiredFields, hasAnyError } = useMemo(() => {
    const requiredFields = { name: true, number: true };

    if (assignment.unitAmountGoal) {
      requiredFields.unit = true;
    }

    const hasRequiredFields = Object.entries(requiredFields).every(([key]) => {
      const value = assignment[key]?.toString().trim();
      return !!value;
    });

    const hasAnyError = !hasRequiredFields;

    return { requiredFields, hasAnyError };
  }, [assignment]);

  const isRequired = (name) => requiredFields[name] || false;
  const hasError = (name) =>
    isRequired(name) && !assignment[name]?.toString().trim();

  const titleIcon = hasAnyError ? (
    <Icon
      path={mdiAlertCircleOutline}
      size={1}
      color={theme.palette.error.main}
    />
  ) : hasChanges ? (
    <Icon path={mdiAlertOutline} size={1} color={theme.palette.warning.main} />
  ) : null;

  return (
    <>
      <ActionContainer
        title={`${
          showAssignmentNumberPath ? `${numberPath}` : `${assignment.number}`
        }${assignment.number ? '.' : ''} ${assignment.name}`}
        closable
        closableDisabled={hasAnyError}
        open={open}
        setOpen={handleOpen}
        color="main"
        actionIcon={<Icon path={mdiPlus} size={1} />}
        onAction={(e) => addChildAssignment(e)}
        popperIcon={<Icon path={mdiDotsVertical} size={1} />}
        popperChildren={<Icon path={mdiDelete} size={1} color="red" />}
        onPopper={() => handleDeleteAssignment(assignment)}
        titleIcon={titleIcon}
        titleIconAlign="right"
      >
        <Grid item xs={4}>
          <TextInput
            label={t('project_dialog.assignment_number')}
            name="number"
            fullWidth
            value={assignment.number}
            onChange={onChange}
            disabled={loading}
            required={isRequired('number')}
            error={hasError('number')}
          />
        </Grid>
        <Grid item xs={8}>
          <TextInput
            label={t('project_dialog.assignment_name')}
            name="name"
            fullWidth
            value={assignment.name}
            onChange={onChange}
            disabled={loading}
            required={isRequired('name')}
            error={hasError('name')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            label={t('project_dialog.assignment_amount')}
            name="unitAmountGoal"
            fullWidth
            value={assignment.unitAmountGoal}
            onChange={onChange}
            disabled={loading}
            type="number"
            required={isRequired('unitAmountGoal')}
            error={hasError('unitAmountGoal')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            label={t('project_dialog.assignment_unit')}
            name="unit"
            fullWidth
            value={assignment.unit}
            onChange={onChange}
            disabled={loading}
            required={isRequired('unit')}
            error={hasError('unit')}
          />
        </Grid>
        <Grid item xs={4}>
          <TextInput
            label={t('project_dialog.assignment_unit_price')}
            name="unitPrice"
            fullWidth
            value={assignment.unitPrice}
            onChange={onChange}
            disabled={loading}
            type="number"
            required={isRequired('unitPrice')}
            error={hasError('unitPrice')}
          />
        </Grid>
        <Grid
          item
          xs={12}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <SwitchItem
            title={t('project_dialog.assignment_show_in_clocking')}
            name="hideInClocking"
            checked={!assignment.hideInClocking}
            onChange={(checked) => onChangeNonEvent('hideInClocking', !checked)}
            disabled={
              loading ||
              (parentAssignment &&
                parentAssignment.hideInClocking &&
                assignment.hideInClocking)
            }
          />
        </Grid>
        {/* <Grid
          item
          xs={12}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <SwitchItem
            title="Näytä littera kustannusseurannassa"
            name="hideInExpenses"
            checked={!assignment.hideInExpenses}
            onChange={(checked) => onChangeNonEvent('hideInExpenses', !checked)}
            disabled={loading}
          />
        </Grid> */}
        <Grid
          item
          xs={12}
          container
          justifyContent="flex-end"
          alignItems="center"
        >
          <SwitchItem
            title={t('project_dialog.assignment_show_in_expense_reports')}
            name="hideInExpenseReports"
            checked={!assignment.hideInExpenseReports}
            onChange={(checked) =>
              onChangeNonEvent('hideInExpenseReports', !checked)
            }
            disabled={
              loading ||
              (parentAssignment &&
                parentAssignment.hideInExpenseReports &&
                assignment.hideInExpenseReports)
            }
          />
        </Grid>
      </ActionContainer>
      {childAssignments &&
        childAssignments
          .sort((a, b) => a?.number - b?.number)
          .map((childAssignment, index) => (
            <Grid
              container
              item
              key={childAssignment.id}
              style={{
                paddingLeft: theme.spacing(2),
              }}
            >
              <AssignmentItem
                name={childAssignment.name}
                number={childAssignment.number}
                assignment={childAssignment}
                childAssignments={assignments.filter(
                  (child) => child.parent === childAssignment.id
                )}
                assignments={assignments}
                handleChangeAssignment={handleChangeAssignment}
                handleChangeValueOfAssignmentAndAllChildren={
                  handleChangeValueOfAssignmentAndAllChildren
                }
                handleAddAssignment={handleAddAssignment}
                showAssignmentNumberPath={showAssignmentNumberPath}
                parentNumber={numberPath}
                parentAssignment={assignment}
                handleDeleteAssignment={handleDeleteAssignment}
                openAssignmentsArray={openAssignmentsArray}
                setOpenAssignmentsArray={setOpenAssignmentsArray}
                updatedAssignmentIds={updatedAssignmentIds}
              />
            </Grid>
          ))}
    </>
  );
};

export default AssignmentItem;
